import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Upload, 
  Search, 
  Filter, 
  FileText, 
  File, 
  Download,
  Trash2,
  Eye,
  Calendar,
  Tag,
  ExternalLink
} from 'lucide-react';
import { useTheme } from '../../hooks/useTheme';

const Documents: React.FC = () => {
  const { colors } = useTheme();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  const mockDocuments = [
    {
      id: '1',
      name: 'React Best Practices.pdf',
      type: 'pdf',
      size: 2.4,
      uploadedAt: '2025-01-15',
      tags: ['React', 'Best Practices', 'Frontend'],
      description: 'Comprehensive guide to React development patterns and best practices'
    },
    {
      id: '2',
      name: 'Machine Learning Notes.docx',
      type: 'doc',
      size: 1.8,
      uploadedAt: '2025-01-14',
      tags: ['ML', 'Notes', 'AI'],
      description: 'Personal notes on machine learning algorithms and implementations'
    },
    {
      id: '3',
      name: 'Database Schema.sql',
      type: 'txt',
      size: 0.5,
      uploadedAt: '2025-01-13',
      tags: ['Database', 'SQL', 'Schema'],
      description: 'Database schema definitions and relationships'
    },
    {
      id: '4',
      name: 'Project Requirements.md',
      type: 'md',
      size: 0.3,
      uploadedAt: '2025-01-12',
      tags: ['Requirements', 'Project', 'Documentation'],
      description: 'Detailed project requirements and specifications'
    },
    {
      id: '5',
      name: 'UI Design System.pdf',
      type: 'pdf',
      size: 5.2,
      uploadedAt: '2025-01-11',
      tags: ['Design', 'UI', 'System'],
      description: 'Complete design system with components and guidelines'
    },
    {
      id: '6',
      name: 'API Documentation.txt',
      type: 'txt',
      size: 1.1,
      uploadedAt: '2025-01-10',
      tags: ['API', 'Documentation', 'Backend'],
      description: 'REST API endpoints and usage documentation'
    }
  ];

  const getFileIcon = (type: string) => {
    switch (type) {
      case 'pdf': return <FileText className="w-8 h-8" style={{ color: colors.red }} />;
      case 'doc': return <FileText className="w-8 h-8" style={{ color: colors.blue }} />;
      case 'txt': return <File className="w-8 h-8" style={{ color: colors.green }} />;
      case 'md': return <FileText className="w-8 h-8" style={{ color: colors.yellow }} />;
      default: return <File className="w-8 h-8" style={{ color: colors.subtext0 }} />;
    }
  };

  const formatFileSize = (size: number) => {
    return `${size.toFixed(1)} MB`;
  };

  const allTypes = Array.from(new Set(mockDocuments.map(doc => doc.type)));

  const filteredDocuments = mockDocuments.filter(doc => {
    const matchesSearch = doc.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         doc.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = !selectedType || doc.type === selectedType;
    return matchesSearch && matchesType;
  });

  const DocumentCard: React.FC<{ document: typeof mockDocuments[0] }> = ({ document }) => (
    <div
      className="p-6 rounded-xl border transition-all hover:scale-105 cursor-pointer"
      style={{ 
        backgroundColor: colors.mantle,
        borderColor: colors.surface0
      }}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          {getFileIcon(document.type)}
          <div>
            <h3 className="font-semibold line-clamp-1" style={{ color: colors.text }}>
              {document.name}
            </h3>
            <div className="flex items-center gap-2 mt-1 text-sm" style={{ color: colors.subtext0 }}>
              <span>{document.type.toUpperCase()}</span>
              <span>•</span>
              <span>{formatFileSize(document.size)}</span>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-1">
          <button className="p-1 rounded hover:opacity-80">
            <Eye className="w-4 h-4" style={{ color: colors.subtext0 }} />
          </button>
          <button className="p-1 rounded hover:opacity-80">
            <Download className="w-4 h-4" style={{ color: colors.subtext0 }} />
          </button>
          <button className="p-1 rounded hover:opacity-80">
            <Trash2 className="w-4 h-4" style={{ color: colors.red }} />
          </button>
        </div>
      </div>
      
      <p className="text-sm mb-4 line-clamp-2" style={{ color: colors.subtext0 }}>
        {document.description}
      </p>
      
      <div className="flex items-center justify-between">
        <div className="flex flex-wrap gap-2">
          {document.tags.slice(0, 2).map((tag) => (
            <span
              key={tag}
              className="text-xs px-2 py-1 rounded"
              style={{ 
                backgroundColor: colors.surface0,
                color: colors.text
              }}
            >
              {tag}
            </span>
          ))}
          {document.tags.length > 2 && (
            <span
              className="text-xs px-2 py-1 rounded"
              style={{ 
                backgroundColor: colors.surface0,
                color: colors.subtext0
              }}
            >
              +{document.tags.length - 2}
            </span>
          )}
        </div>
        
        <div className="flex items-center gap-2 text-xs" style={{ color: colors.subtext0 }}>
          <Calendar className="w-3 h-3" />
          {new Date(document.uploadedAt).toLocaleDateString()}
        </div>
      </div>
    </div>
  );

  const DocumentListItem: React.FC<{ document: typeof mockDocuments[0] }> = ({ document }) => (
    <div
      className="p-4 rounded-lg border transition-all hover:opacity-80 cursor-pointer"
      style={{ 
        backgroundColor: colors.mantle,
        borderColor: colors.surface0
      }}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4 flex-1">
          {getFileIcon(document.type)}
          
          <div className="flex-1">
            <h3 className="font-semibold mb-1" style={{ color: colors.text }}>
              {document.name}
            </h3>
            <p className="text-sm mb-2 line-clamp-1" style={{ color: colors.subtext0 }}>
              {document.description}
            </p>
            
            <div className="flex items-center gap-4">
              <div className="flex flex-wrap gap-2">
                {document.tags.slice(0, 3).map((tag) => (
                  <span
                    key={tag}
                    className="text-xs px-2 py-1 rounded"
                    style={{ 
                      backgroundColor: colors.surface0,
                      color: colors.text
                    }}
                  >
                    {tag}
                  </span>
                ))}
              </div>
              
              <div className="flex items-center gap-4 text-sm" style={{ color: colors.subtext0 }}>
                <span>{document.type.toUpperCase()}</span>
                <span>{formatFileSize(document.size)}</span>
                <div className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {new Date(document.uploadedAt).toLocaleDateString()}
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-2 ml-4">
          <button className="p-2 rounded hover:opacity-80">
            <Eye className="w-4 h-4" style={{ color: colors.subtext0 }} />
          </button>
          <button className="p-2 rounded hover:opacity-80">
            <Download className="w-4 h-4" style={{ color: colors.subtext0 }} />
          </button>
          <button className="p-2 rounded hover:opacity-80">
            <Trash2 className="w-4 h-4" style={{ color: colors.red }} />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h1 className="text-3xl font-bold" style={{ color: colors.text }}>
          Documents
        </h1>
        
        <div className="flex items-center gap-2">
          <button 
            onClick={() => navigate('/documents/google-drive')}
            className="px-4 py-2 rounded-lg border font-medium transition-all hover:opacity-90 flex items-center gap-2"
            style={{ 
              backgroundColor: colors.mantle,
              borderColor: colors.surface0,
              color: colors.text
            }}
          >
            <ExternalLink className="w-4 h-4" />
            Google Drive
          </button>
          
          <button 
            onClick={() => navigate('/documents/upload')}
            className="px-4 py-2 rounded-lg font-medium text-white transition-all hover:opacity-90 flex items-center gap-2"
            style={{ backgroundColor: colors.blue }}
          >
            <Upload className="w-4 h-4" />
            Upload Document
          </button>
        </div>
      </div>

      {/* Upload Area */}
      <div 
        className="border-2 border-dashed rounded-xl p-8 text-center transition-colors hover:opacity-80 cursor-pointer"
        style={{ 
          backgroundColor: colors.mantle,
          borderColor: colors.surface1
        }}
        onClick={() => navigate('/documents/upload')}
      >
        <Upload className="w-12 h-12 mx-auto mb-4" style={{ color: colors.subtext0 }} />
        <h3 className="text-lg font-medium mb-2" style={{ color: colors.text }}>
          Drop files here to upload
        </h3>
        <p className="mb-4" style={{ color: colors.subtext0 }}>
          or click to browse your files
        </p>
        <button 
          className="px-4 py-2 rounded-lg font-medium text-white transition-all hover:opacity-90"
          style={{ backgroundColor: colors.blue }}
        >
          Choose Files
        </button>
      </div>

      {/* Filters and Search */}
      <div className="flex flex-col lg:flex-row gap-4">
        <div className="flex-1">
          <div className="relative">
            <Search 
              className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5"
              style={{ color: colors.subtext0 }}
            />
            <input
              type="text"
              placeholder="Search documents..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 rounded-lg border focus:outline-none focus:ring-2 transition-colors"
              style={{
                backgroundColor: colors.mantle,
                borderColor: colors.surface0,
                color: colors.text
              }}
            />
          </div>
        </div>
        
        <div className="flex items-center gap-4">
          <div className="relative">
            <select
              value={selectedType || ''}
              onChange={(e) => setSelectedType(e.target.value || null)}
              className="pl-3 pr-8 py-3 rounded-lg border focus:outline-none focus:ring-2 transition-colors appearance-none"
              style={{
                backgroundColor: colors.mantle,
                borderColor: colors.surface0,
                color: colors.text
              }}
            >
              <option value="">All Types</option>
              {allTypes.map((type) => (
                <option key={type} value={type}>{type.toUpperCase()}</option>
              ))}
            </select>
            <Tag 
              className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 pointer-events-none"
              style={{ color: colors.subtext0 }}
            />
          </div>
          
          <div 
            className="flex items-center rounded-lg border"
            style={{ 
              backgroundColor: colors.mantle,
              borderColor: colors.surface0
            }}
          >
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-l-lg transition-colors ${
                viewMode === 'grid' ? 'text-white' : ''
              }`}
              style={{
                backgroundColor: viewMode === 'grid' ? colors.blue : 'transparent',
                color: viewMode === 'grid' ? 'white' : colors.text
              }}
            >
              <div className="w-4 h-4 grid grid-cols-2 gap-0.5">
                <div className="bg-current rounded-sm"></div>
                <div className="bg-current rounded-sm"></div>
                <div className="bg-current rounded-sm"></div>
                <div className="bg-current rounded-sm"></div>
              </div>
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-r-lg transition-colors ${
                viewMode === 'list' ? 'text-white' : ''
              }`}
              style={{
                backgroundColor: viewMode === 'list' ? colors.blue : 'transparent',
                color: viewMode === 'list' ? 'white' : colors.text
              }}
            >
              <div className="w-4 h-4 flex flex-col gap-1">
                <div className="bg-current h-0.5 rounded"></div>
                <div className="bg-current h-0.5 rounded"></div>
                <div className="bg-current h-0.5 rounded"></div>
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Active Filters */}
      {selectedType && (
        <div className="flex items-center gap-2">
          <span className="text-sm" style={{ color: colors.subtext0 }}>
            Filtered by:
          </span>
          <span
            className="text-sm px-3 py-1 rounded-full flex items-center gap-2"
            style={{ 
              backgroundColor: colors.surface0,
              color: colors.text
            }}
          >
            {selectedType.toUpperCase()}
            <button
              onClick={() => setSelectedType(null)}
              className="hover:opacity-80"
            >
              ×
            </button>
          </span>
        </div>
      )}

      {/* Documents Display */}
      {viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredDocuments.map((document) => (
            <DocumentCard key={document.id} document={document} />
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          {filteredDocuments.map((document) => (
            <DocumentListItem key={document.id} document={document} />
          ))}
        </div>
      )}

      {/* Empty State */}
      {filteredDocuments.length === 0 && (
        <div className="text-center py-12">
          <div 
            className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center"
            style={{ backgroundColor: colors.surface0 }}
          >
            <FileText className="w-8 h-8" style={{ color: colors.subtext0 }} />
          </div>
          <h3 className="text-lg font-medium mb-2" style={{ color: colors.text }}>
            No documents found
          </h3>
          <p className="mb-4" style={{ color: colors.subtext0 }}>
            {searchTerm || selectedType 
              ? 'Try adjusting your search or filters'
              : 'Upload your first document to get started'
            }
          </p>
          <button 
            onClick={() => navigate('/documents/upload')}
            className="px-4 py-2 rounded-lg font-medium text-white transition-all hover:opacity-90"
            style={{ backgroundColor: colors.blue }}
          >
            Upload Document
          </button>
        </div>
      )}
    </div>
  );
};

export default Documents;